import type { RankingInfo } from '@tanstack/match-sorter-utils';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ChevronsDown,
  ChevronsUp,
  X,
  Search,
  Filter,
  RotateCcw,
  Eye,
  EyeOff,
  Columns3,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input.tsx';
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils.ts';
import { DataTableFacetedFilter } from '@/UI/TableShadcn/filter.tsx';

import { DataTablePagination } from './pagination';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn('w-full caption-bottom text-sm', className)}
    {...props}
  />
));
Table.displayName = 'Table';

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }

  interface FilterMeta {
    itemRank: RankingInfo;
  }
}
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isPaginated?: boolean;
  isTopToolbar?: boolean;
  isFooter?: boolean;
  heightTable?: string;
  pageSize?: number;
  actionRow?: (item: TData) => void;
  isRowSelection?: boolean;
  ActionSelectComponent?: (items: TData[]) => React.ReactNode;
  ChildHeader?: (items: TData[]) => React.ReactNode;
  columnsSelectFilters?: {
    columnId: string;
    options: { value: string; label: string }[];
    title: string;
  }[];
  visibleColumns?: (keyof TData | string)[];
}

// A typical debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <div className="relative">
      <Search
        className={cn(
          'absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
          'pointer-events-none text-muted-foreground/70',
        )}
      />
      <Input
        {...props}
        className={cn(
          'h-9 pl-9 pr-3',
          'bg-background/50 backdrop-blur-sm',
          'border-border/50 dark:border-slate-700/50',
          'placeholder:text-muted-foreground/60',
          'focus:border-primary/50 focus:ring-2 focus:ring-primary/20',
          'transition-all duration-200',
        )}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isTopToolbar = true,
  isPaginated = true,
  heightTable = '20vh',
  pageSize = 20,
  isFooter = false,
  actionRow = () => {},
  isRowSelection = false,
  ActionSelectComponent = () => null,
  ChildHeader = () => null,
  columnsSelectFilters = [],
  visibleColumns = [],
}: DataTableProps<TData, TValue>) {
  // Memoize data to improve performance
  const memoizedData = useMemo(() => data, [data]);

  // Stabilize visibleColumns reference to prevent infinite re-renders
  const stableVisibleColumns = useMemo(() => visibleColumns, [visibleColumns]);

  // 1️⃣ Compute initial column visibility based on `visibleColumns`
  const initialHiddenColumns = useMemo(() => {
    return columns.reduce((acc, column) => {
      const colKey = (column as any).accessorKey || column.id;
      if (colKey && stableVisibleColumns.length > 0) {
        // We invert the logic - if a column is in visibleColumns, we hide it (set to false)
        acc[colKey] = !stableVisibleColumns.includes(colKey as never);
      }
      return acc;
    }, {} as VisibilityState);
  }, [columns, stableVisibleColumns]);

  // State
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(initialHiddenColumns);

  // Apply column visibility when visibleColumns or columns change
  useEffect(() => {
    if (stableVisibleColumns.length > 0) {
      setColumnVisibility(initialHiddenColumns);
    }
  }, [initialHiddenColumns, stableVisibleColumns]);

  // Create table instance with memoized data
  const table = useReactTable({
    data: memoizedData,
    columns,
    initialState: {
      columnVisibility: initialHiddenColumns,
      ...(isPaginated && {
        pagination: { pageSize },
      }),
    },
    state: {
      rowSelection,
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
    enableRowSelection: isRowSelection,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: 'includesString',
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    enableColumnFilters: true,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(isPaginated ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    manualPagination: !isPaginated,
    filterFns: {
      CustomFilter: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (!value) return false;
        return String(value)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      },
      BooleanFilter: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (!value) return false;
        return String(value)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      },
    } as Record<string, FilterFn<TData>>,
    debugTable: false,
    autoResetAll: false,
    autoResetPageIndex: false,
  });

  // Check if any filters are applied
  const isFiltered = table.getState().columnFilters.length > 0;

  return (
    <div className="flex flex-col p-0">
      {/* Enhanced Top Toolbar */}
      {isTopToolbar && (
        <Card
          className={cn(
            'mb-4 border-0 p-4',
            'bg-gradient-to-r from-background via-background/95 to-muted/5',
            'dark:from-slate-900 dark:via-slate-900/95 dark:to-slate-800/10',
            'border-b border-border/30 shadow-sm',
          )}
        >
          <div className="flex w-full items-center justify-between gap-4">
            {/* Left Section - Search and Filters */}
            <div className="flex flex-1 items-center gap-3">
              {/* Enhanced Search Input */}
              <div className="relative max-w-sm flex-1">
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={(value) => setGlobalFilter(String(value))}
                  placeholder="بحث في الجدول..."
                />
                {globalFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setGlobalFilter('')}
                    className={cn(
                      'absolute right-1 top-1/2 -translate-y-1/2',
                      'h-6 w-6 rounded-full p-0',
                      'hover:bg-destructive/10 hover:text-destructive',
                      'transition-all duration-200',
                    )}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-2">
                {columnsSelectFilters?.length > 0 &&
                  columnsSelectFilters.map((x) => {
                    const column = table.getColumn(x.columnId);
                    return (
                      column && (
                        <DataTableFacetedFilter
                          key={x.columnId}
                          column={column}
                          title={x.title}
                          options={x.options}
                        />
                      )
                    );
                  })}

                {/* Active Filters Badge */}
                {(isFiltered || globalFilter) && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'gap-1 px-2 py-1',
                      'bg-primary/10 text-primary',
                      'dark:bg-primary/20 dark:text-primary',
                      'border border-primary/20',
                    )}
                  >
                    <Filter className="h-3 w-3" />
                    <span className="text-xs font-medium">
                      {table.getState().columnFilters.length +
                        (globalFilter ? 1 : 0)}{' '}
                      نشط
                    </span>
                  </Badge>
                )}

                {/* Reset Filters Button */}
                {(isFiltered || globalFilter) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      table.resetColumnFilters();
                      setGlobalFilter('');
                    }}
                    className={cn(
                      'h-8 gap-1.5',
                      'text-muted-foreground hover:text-destructive',
                      'hover:bg-destructive/10 dark:hover:bg-destructive/20',
                      'transition-all duration-200',
                    )}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span className="text-xs">مسح الكل</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Right Section - Actions and View Options */}
            <div className="flex items-center gap-2">
              {/* Child Header Component */}
              {ChildHeader(memoizedData)}

              {/* Column Visibility Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'h-9 gap-2',
                      'bg-background/50 hover:bg-muted/50',
                      'border-border/50 dark:border-slate-700/50',
                      'group transition-all duration-200',
                      'hover:scale-105 hover:shadow-md',
                    )}
                  >
                    <Columns3 className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
                    <span className="hidden text-xs font-medium lg:inline-block">
                      أعمدة
                    </span>
                    <Badge
                      variant="outline"
                      className="ml-1 h-4 bg-primary/10 px-1 text-[10px] text-primary"
                    >
                      {
                        table
                          .getAllColumns()
                          .filter((col) => col.getIsVisible()).length
                      }
                    </Badge>
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className={cn(
                    'w-[200px]',
                    'bg-background/95 backdrop-blur-md',
                    'border-border/50 dark:border-slate-700/50',
                    'shadow-lg',
                  )}
                >
                  <DropdownMenuLabel className="flex items-center gap-2 text-sm font-semibold">
                    <Eye className="h-4 w-4 text-primary" />
                    إظهار/إخفاء الأعمدة
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border/50" />

                  {/* Show/Hide All Buttons */}
                  <div className="flex gap-1 p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={() => table.toggleAllColumnsVisible(true)}
                    >
                      <Eye className="mr-1 h-3 w-3" />
                      إظهار الكل
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={() => table.toggleAllColumnsVisible(false)}
                    >
                      <EyeOff className="mr-1 h-3 w-3" />
                      إخفاء الكل
                    </Button>
                  </div>

                  <DropdownMenuSeparator className="bg-border/50" />

                  <div className="max-h-[300px] overflow-y-auto">
                    {table
                      .getAllColumns()
                      .filter(
                        (column) =>
                          typeof column.accessorFn !== 'undefined' &&
                          column.getCanHide(),
                      )
                      .map((column) => {
                        // Handle different types of header values
                        let headerText = '';
                        const headerValue = column.columnDef.header;

                        if (typeof headerValue === 'string') {
                          headerText = headerValue;
                        } else if (typeof headerValue === 'function') {
                          // For function headers, use the column ID as fallback text
                          headerText = column.id;
                        } else if (React.isValidElement(headerValue)) {
                          // For React elements, use column ID as fallback
                          headerText = column.id;
                        } else {
                          // Default fallback
                          headerText = column.id;
                        }

                        return (
                          <DropdownMenuCheckboxItem
                            key={column.id}
                            className={cn(
                              'cursor-pointer capitalize',
                              'hover:bg-primary/10 dark:hover:bg-primary/20',
                              'transition-colors duration-200',
                            )}
                            checked={column.getIsVisible()}
                            onCheckedChange={(value) =>
                              column.toggleVisibility(value)
                            }
                          >
                            <span className="text-xs font-medium">
                              {headerText}
                            </span>
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Stats Bar */}
          {(table.getFilteredRowModel().rows.length !== data.length ||
            isRowSelection) && (
            <div
              className={cn(
                'mt-3 border-t border-border/30 pt-3',
                'flex items-center justify-between',
              )}
            >
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="gap-1.5 bg-background/50">
                  <span className="text-xs text-muted-foreground">إجمالي:</span>
                  <span className="text-xs font-bold">{data.length}</span>
                </Badge>

                {table.getFilteredRowModel().rows.length !== data.length && (
                  <Badge
                    variant="outline"
                    className="gap-1.5 border-primary/30 bg-primary/10 text-primary"
                  >
                    <span className="text-xs">مفلتر:</span>
                    <span className="text-xs font-bold">
                      {table.getFilteredRowModel().rows.length}
                    </span>
                  </Badge>
                )}

                {isRowSelection && Object.keys(rowSelection).length > 0 && (
                  <Badge
                    variant="outline"
                    className="gap-1.5 border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400"
                  >
                    <span className="text-xs">محدد:</span>
                    <span className="text-xs font-bold">
                      {Object.keys(rowSelection).length}
                    </span>
                  </Badge>
                )}
              </div>

              {isRowSelection && Object.keys(rowSelection).length > 0 && (
                <div className="flex items-center gap-2">
                  {ActionSelectComponent(
                    table
                      .getFilteredSelectedRowModel()
                      .rows.map((row) => row.original),
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Table */}
      <div
        className="overflow-x-auto rounded-lg border shadow-md"
        style={{ height: heightTable }}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                className="sticky top-0 z-20 border-b-2"
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    className="bg-white p-0 dark:bg-black"
                    key={header.id}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex items-center justify-center',
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : '',
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: <ChevronsUp className="h-3.5 w-3.5" />,
                          desc: <ChevronsDown className="h-3.5 w-3.5" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isSelected = row.getIsSelected();
                return (
                  <TableRow
                    onDoubleClick={() => actionRow(row?.original)}
                    key={row.id}
                    data-state={isSelected && '-'}
                    className={cn(
                      'hover:bg-primary/10 dark:hover:bg-primary/10',
                      isSelected && 'bg-primary/10 dark:bg-primary/10',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="p-0" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  لا يوجد بيانات
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {isFooter && (
            <TableFooter>
              {table.getFooterGroups().map((footerGroup) => (
                <TableRow
                  key={footerGroup.id}
                  className="sticky -bottom-0 z-20 border-b-2 bg-gray-200 p-0 dark:bg-gray-900"
                >
                  {footerGroup.headers.map((header) => (
                    <TableCell key={header.id} className="p-0">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
      </div>

      {/* Pagination */}
      {isPaginated && (
        <DataTablePagination
          isSelected={isRowSelection}
          ActionSelectComponent={ActionSelectComponent}
          table={table}
        />
      )}
    </div>
  );
}
